import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useStepbarStore } from '@/store/useStepbarStore'
import { useIsActiveBreakPoint } from './useIsActiveBreakPoint'
import { useStepbarSteps } from './useStepbarSteps'

interface UseStepStatusArgs {
  isCompleted: boolean // TODO: remover prop
  targetPath: string
  targetStep: string
}

export const useStepStatus = ({
  targetPath,
  targetStep
}: UseStepStatusArgs) => {
  const pathName = useLocation().pathname
  const pathNameRef = useRef('')
  const selectedStep = useStepbarStore((state) => state.selectedStep)
  const stepWithError = useStepbarStore((state) => state.stepWithError)
  const { steps } = useStepbarSteps()
  const targetStepStatus = steps.find((step) => step.path === targetPath)
  const setSelectedStep = useStepbarStore((state) => state.setSelectedStep)
  const isMobile = useIsActiveBreakPoint('medium', 'max')

  const status = useMemo(() => {
    if (targetStepStatus?.disabled) {
      return 'inactive'
    }

    if (targetPath === stepWithError) {
      return 'error'
    }

    if (isMobile && selectedStep === targetStep) {
      return 'selected'
    }

    if (pathName === targetPath) {
      return 'active'
    }

    if (targetStepStatus?.completed) {
      return 'completed'
    }

    return 'inactive'
  }, [
    stepWithError,
    isMobile,
    pathName,
    selectedStep,
    targetPath,
    targetStep,
    targetStepStatus?.completed,
    targetStepStatus?.disabled
  ])

  const handleStepChange = useCallback(() => {
    if (pathName === pathNameRef.current) {
      return
    }

    setSelectedStep(null)

    pathNameRef.current = pathName
  }, [pathName, setSelectedStep])

  useEffect(() => {
    handleStepChange()
  }, [handleStepChange])

  return status
}
